import type { Domain } from '@/types'
import { Badge } from '@/components/ui/badge'

interface DomainBadgeProps {
  domain?: Domain | null
  domainName?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'solid'
  className?: string
}

const domainColors: Record<string, string> = {
  'business': '#3B82F6',
  'tech': '#10B981',
  'psychology': '#8B5CF6',
  'philosophy': '#F59E0B',
  'science': '#EF4444',
  'art': '#EC4899',
  'health': '#06B6D4'
}

const domainIcons: Record<string, string> = {
  'business': '💼',
  'tech': '⚡',
  'psychology': '🧠',
  'philosophy': '🤔',
  'science': '🔬',
  'art': '🎨',
  'health': '💚'
}

export default function DomainBadge({
  domain,
  domainName,
  size = 'md',
  variant = 'default',
  className = ''
}: DomainBadgeProps) {
  const name = domain?.name || domainName
  const color = domain?.color || domainColors[name?.toLowerCase() || ''] || '#8B5CF6'
  const icon = domain?.icon || domainIcons[name?.toLowerCase() || ''] || '🔮'

  if (!name) {
    return null
  }

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm'
  }

  return (
    <Badge
      variant="outline"
      className={`
        inline-flex items-center gap-1.5 rounded-lg font-bold uppercase tracking-wider font-mono
        transition-all duration-200 border
        ${sizeClasses[size]}
        ${className}
      `}
      style={{
        backgroundColor: variant === 'solid'
          ? color
          : variant === 'default' ? `${color}1A` : 'transparent',
        borderColor: color,
        color: variant === 'solid' ? '#FFFFFF' : color,
      }}
      title={`Domain: ${name}`}
    >
      <span className="opacity-80">{icon}</span>
      <span>{name}</span>
    </Badge>
  )
}
